import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Checkbox,
  ListItemText,
  Autocomplete,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Storefront,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { request } from '../../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { Brand, VendorFormData } from '../../../types';
import { baseUrlImage } from '../../../../../utils/apiClient';

const commonVendorTypes = ['Supplier', 'Manufacturer', 'Distributor', 'Retailer', 'Service Provider', 'Wholesaler'];
const phoneLabels = ['mobile', 'work', 'home', 'other'];

const EditVendor = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [newPhone, setNewPhone] = useState({ label: 'mobile', phoneNo: '' });
  const [newEmail, setNewEmail] = useState('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<VendorFormData>({
    defaultValues: {
      vendorName: '',
      nickName: '',
      type: [],
      contact: {
        phone: [],
        email: [],
      },
      location: '',
      address: {
        line1: '',
        city: '',
        state: '',
        country: '',
        pinCode: '',
      },
      brands: [], // Now array of objects
      gstNumber: '',
      status: 'active',
    },
    mode: 'onChange',
  });

  const formValues = watch();

  // Fetch vendor data and brands
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchLoading(true);

        // Fetch vendor data
        const vendorResponse = await request({
          url: `/vendor/get-vendor/${id}`,
          method: 'get',
        });

        // Fetch brands
        const brandsResponse = await request({
          url: '/brand/get-by-manufacture-type/Parts',
          method: 'get',
        });

        if (vendorResponse.vendor) {
          const vendor = vendorResponse.vendor;
          // Set form values with fetched vendor data
          reset({
            vendorName: vendor.vendorName,
            nickName: vendor.nickName || '',
            type: vendor.type,
            contact: {
              phone: vendor.contact.phone,
              email: vendor.contact.email,
            },
            location: vendor.location,
            address: {
              line1: vendor.address?.line1 || '',
              city: vendor.address?.city || '',
              state: vendor.address?.state || '',
              country: vendor.address?.country || '',
              pinCode: vendor.address?.pinCode || '',
            },
            brands: vendor.brands || [], // Handle brands as array of objects
            gstNumber: vendor.gstNumber || '', // Convert null to empty string for form
            status: vendor.status,
          });
        }

        if (brandsResponse.brands) {
          setBrands(brandsResponse.brands);
        }
      } catch (error: any) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load vendor data');
        navigate('/vendors/vendor');
      } finally {
        setFetchLoading(false);
        setBrandsLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, reset, navigate]);

  const onSubmit = async (data: VendorFormData) => {
    try {
      setLoading(true);

      // Prepare GST number - convert empty string to null
      const submitData = {
        ...data,
        gstNumber: data.gstNumber && data.gstNumber.trim() !== ''
          ? data.gstNumber.trim().toUpperCase()
          : null
      };

      const response = await request({
        url: `/vendor/update-vendor/${id}`,
        method: 'put',
        data: submitData,
      });

      if (response.status === 'success') {
        toast.success('Vendor updated successfully!');
        navigate('/vendors/vendor');
      }
    } catch (error: any) {
      toast.error(error || 'Failed to update vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhone = () => {
    if (newPhone.phoneNo && /^[6-9]\d{9}$/.test(newPhone.phoneNo)) {
      const updatedPhones = [...formValues.contact.phone, { ...newPhone }];
      setValue('contact.phone', updatedPhones, { shouldValidate: true });
      setNewPhone({ label: 'mobile', phoneNo: '' });
    }
  };

  const handleRemovePhone = (index: number) => {
    const updatedPhones = formValues.contact.phone.filter((_, i) => i !== index);
    setValue('contact.phone', updatedPhones, { shouldValidate: true });
  };

  const handleAddEmail = () => {
    if (newEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      const updatedEmails = [...formValues.contact.email, newEmail.toLowerCase()];
      setValue('contact.email', updatedEmails, { shouldValidate: true });
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (index: number) => {
    const updatedEmails = formValues.contact.email.filter((_, i) => i !== index);
    setValue('contact.email', updatedEmails, { shouldValidate: true });
  };

  const handleBrandsChange = (event: any, newValue: Brand[]) => {
    // Convert selected brands to the required format
    const formattedBrands = newValue.map(brand => ({
      brandName: brand.name,
      brandLogo: brand.brandLogo || ''
    }));
    setValue('brands', formattedBrands, { shouldValidate: true });
  };

  const handleTypeChange = (types: string[]) => {
    setValue('type', types, { shouldValidate: true });
  };

  const handleGstNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setValue('gstNumber', value, { shouldValidate: true });
  };

  const handlePhoneLabelChange = (label: string) => {
    setNewPhone(prev => ({ ...prev, label }));
  };

  const handlePhoneNumberChange = (phoneNo: string) => {
    setNewPhone(prev => ({ ...prev, phoneNo: phoneNo.replace(/\D/g, '') }));
  };

  const isFormValid = isValid &&
    formValues.contact.phone.length > 0 &&
    formValues.brands.length > 0 &&
    formValues.type.length > 0;

  // Filter brands to only show parts manufacturers
  const partsBrands = brands.filter(brand =>
    brand.manufactureType.includes('Parts')
  );

  // Get selected brand objects for display
  const selectedBrands = formValues.brands.map(brandObj => 
    partsBrands.find(brand => brand.name === brandObj.brandName)
  ).filter(Boolean) as Brand[];

  // Safe function to get brand initial
  const getBrandInitial = (brandName: string | undefined) => {
    if (!brandName || typeof brandName !== 'string') return '?';
    return brandName.charAt(0).toUpperCase();
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading vendor data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Box sx={{ margin: '0 auto' }}>
        {/* Header */}
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>

                <Storefront color="primary" sx={{ fontSize: 24 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Edit Vendor
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Update vendor profile details
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={3}>
          <CardContent sx={{ p: 2 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Basic Information */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Vendor Name *"
                    {...register('vendorName', {
                      required: 'Vendor name is required',
                      minLength: { value: 1, message: 'Vendor name is required' }
                    })}
                    error={!!errors.vendorName}
                    helperText={errors.vendorName?.message}
                    placeholder="Company name"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Nick Name"
                    {...register('nickName')}
                    placeholder="Short name"
                  />
                </Box>

                {/* Vendor Type with Checkbox Select */}
                <Box mb={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vendor Type *</InputLabel>
                    <Select
                      multiple
                      value={formValues.type}
                      onChange={(e) => handleTypeChange(e.target.value as string[])}
                      label="Vendor Type *"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {commonVendorTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          <Checkbox checked={formValues.type.indexOf(type) > -1} />
                          <ListItemText primary={type} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormHelperText error={formValues.type.length === 0}>
                    {formValues.type.length === 0 && 'Select at least one vendor type'}
                  </FormHelperText>
                </Box>
              </Box>

              {/* Contact Information */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" gap={1} mb={2}>
                  {/* Phone Numbers */}
                  <Box flex={1}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Phone Numbers *
                    </Typography>
                    <Box display="flex" gap={0.5} mb={1}>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={phoneLabels}
                        value={newPhone.label}
                        onChange={(event, newValue) => {
                          handlePhoneLabelChange(newValue || 'mobile');
                        }}
                        onInputChange={(event, newValue) => {
                          handlePhoneLabelChange(newValue || 'mobile');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Label"
                            sx={{ minWidth: 100 }}
                          />
                        )}
                        sx={{ width: "30%" }}
                      />

                      <TextField
                        size="small"
                        value={newPhone.phoneNo}
                        onChange={(e) => handlePhoneNumberChange(e.target.value)}
                        placeholder="10-digit number"
                        error={!!newPhone.phoneNo && !/^[6-9]\d{9}$/.test(newPhone.phoneNo)}
                        helperText={!!newPhone.phoneNo && !/^[6-9]\d{9}$/.test(newPhone.phoneNo) ? 'Invalid' : ''}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPhone())}
                        fullWidth
                      />

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddPhone}
                        disabled={!newPhone.phoneNo || !/^[6-9]\d{9}$/.test(newPhone.phoneNo)}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        <AddIcon fontSize="small" />
                      </Button>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {formValues.contact.phone.map((phone, index) => (
                        <Chip
                          key={index}
                          label={`${phone.label}: ${phone.phoneNo}`}
                          size="small"
                          onDelete={() => handleRemovePhone(index)}
                          color="primary"
                          deleteIcon={<DeleteIcon />}
                        />
                      ))}
                    </Box>
                    <FormHelperText error={formValues.contact.phone.length === 0}>
                      {formValues.contact.phone.length === 0 && 'Add at least one phone number'}
                    </FormHelperText>
                  </Box>

                  {/* Email Addresses */}
                  <Box flex={1}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Email Addresses
                    </Typography>
                    <Box display="flex" gap={0.5} mb={1}>
                      <TextField
                        size="small"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="email@domain.com"
                        error={!!newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)}
                        helperText={!!newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail) ? 'Invalid' : ''}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                        fullWidth
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddEmail}
                        disabled={!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        <AddIcon fontSize="small" />
                      </Button>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {formValues.contact.email.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          size="small"
                          onDelete={() => handleRemoveEmail(index)}
                          color="secondary"
                          deleteIcon={<DeleteIcon />}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Location & Address */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Location & Address
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Location *"
                    {...register('location', {
                      required: 'Location is required',
                      minLength: { value: 1, message: 'Location is required' }
                    })}
                    error={!!errors.location}
                    helperText={errors.location?.message}
                    placeholder="Business location"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="GST Number (Optional)"
                    {...register('gstNumber', {
                      pattern: {
                        value: /^$|^[0-9A-Z]{15}$/, // Allow empty string or 15 characters
                        message: 'GST number must be 15 characters alphanumeric or empty'
                      }
                    })}
                    onChange={handleGstNumberChange}
                    error={!!errors.gstNumber}
                    helperText={errors.gstNumber?.message || 'Leave empty if not available'}
                    placeholder="15-character GST (optional)"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Box>

                <TextField
                  size="small"
                  fullWidth
                  label="Address Line 1"
                  {...register('address.line1')}
                  placeholder="Street address"
                  sx={{ mb: 1 }}
                />

                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label="City"
                    {...register('address.city')}
                    placeholder="City"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="State"
                    {...register('address.state')}
                    placeholder="State"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="PIN Code"
                    {...register('address.pinCode')}
                    placeholder="PIN Code"
                  />
                </Box>

                <TextField
                  size="small"
                  fullWidth
                  label="Country"
                  {...register('address.country')}
                  placeholder="Country"
                />
              </Box>

              {/* Brands Selection */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Associated Brands *
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box>
                  <FormControl fullWidth size="small">
                    <Autocomplete
                      multiple
                      options={partsBrands}
                      value={selectedBrands}
                      onChange={handleBrandsChange}
                      loading={brandsLoading}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => (
                        <MenuItem {...props}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {option.brandLogo && (
                              <Avatar 
                                src={option.brandLogo ? baseUrlImage + option.brandLogo : 'https://cdn-icons-png.freepik.com/512/9942/9942310.png'} 
                                alt={option.name}
                                sx={{ width: 24, height: 24 }}
                              />
                            )}
                            <Typography>{option.name}</Typography>
                          </Box>
                        </MenuItem>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size='small'
                          label="Select Brands"
                          placeholder="Choose brands..."
                          error={!!errors.brands}
                          helperText={errors.brands?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {brandsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              option.brandLogo ? (
                                <Avatar 
                                  src={option.brandLogo ? baseUrlImage + option.brandLogo : 'https://cdn-icons-png.freepik.com/512/9942/9942310.png'} 
                                  alt={option.name}
                                  sx={{ width: 24, height: 24 }}
                                />
                              ) : (
                                <Avatar 
                                  sx={{ 
                                    width: 24, 
                                    height: 24, 
                                    bgcolor: 'secondary.main',
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  {getBrandInitial(option.name)}
                                </Avatar>
                              )
                            }
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            color="secondary"
                            variant="filled"
                          />
                        ))
                      }
                    />
                  </FormControl>
                  <FormHelperText error={formValues.brands.length === 0}>
                    {formValues.brands.length === 0 && 'Select at least one brand'}
                  </FormHelperText>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Showing parts manufacturers only
                  </Typography>
                </Box>
              </Box>

              {/* Status & Submit */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    {...register('status')}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>

                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={loading || !isFormValid}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                  >
                    {loading ? 'Updating...' : 'Update Vendor'}
                  </Button>
                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default EditVendor;